// @ts-strict-ignore
import { Parameters, WagoApiClient } from '@shared/service/wago-api.client';
import { ArticleMetadataVO, ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { OBJRepresentationDataVO } from '@shared/vo/representation.vo';
import { ExportArticleDTO } from '@article/model/vo/export-article.dto';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { ArticleAccessoryEntryVO } from '@article/model/vo/article-accessory-info-vo';
import { map, tap } from 'rxjs/operators';
import { ModelConversionService } from '@shared/convert/model-conversion.service';
import { ArticleDataService } from '@article/service/article-data.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { TreeAssistantNode } from '../right-panel/tree-structure/tree-assistant-node';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { NodeType } from '../right-panel/tree-structure/node-type.enum';
import { ArticleUtils } from '@article/util/article-utils';
import { CentreArticleLocationVO } from '@project/components/article/model/vo/centre-article-location.vo';
import { ProjectModel } from '@project/model/project.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { ArticleObject3DManager } from '@article/manager/article-object-3d.manager';
import { ArticleManager } from '@article/manager/article.manager';
import { ProjectArticleManager } from '@project/components/article/manager/project-article.manager';
import { OverlayBackground, OverlayType } from '@shared/overlay/overlay.type';
import { PropertyDefTypes } from '../components/property/add-edit-property-def/property-def-types.enum';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { StringUtils } from '@shared/util/string-utils';
import { CommonUtils } from '@shared/util/common.utils';
import { ArticleEndPlateInfoVO } from '@article/model/vo/article-end-plate-info-vo';
import { InsertArticleService } from '@project/components/article/service/insert-article.service';
import { Log } from '@shared/debug/log';
import { computed, Injectable, signal } from '@angular/core';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { MetaElementVO } from '@shared/vo/meta-element.vo';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';
import { AdmintoolComponentsPresenter } from '../components/presenter/admintool-components.presenter';
import { UpdateCompositeArticleResult } from '@article/model/vo/update-composite-article-result.vo';
import { QueryParamsService } from '@services/query-params/query-params.service';
import { ToastrService } from 'ngx-toastr';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { GlobalContainer } from '@3d-scene/model3d/global-container';
import { ProjectChanged, ProjectChangedType } from '@project/model/project-changed';

interface ArticleWithMultiConnections {
  article: ArticleVO,
  connectionIdentifiers: string[]
}

@Injectable({ providedIn: 'root' })
export class AdminToolService {
  private articleSavedSubject = new Subject<void>();
  private articleNoLoadedSubject = new Subject<string>();
  public articleSaved$ = this.articleSavedSubject.asObservable();
  public articleNoLoaded$ = this.articleNoLoadedSubject.asObservable();

  public currentArticleVO = signal<ArticleVO | undefined>(undefined);
  public isArticleLoaded = computed(() => !!this.currentArticleVO());
  public isCompositeArticle = computed(() => ArticleUtils.isCompositeArticle(this.currentArticleVO()));

  constructor(
    private readonly modelConversionService: ModelConversionService,
    private readonly alertHelper: AlertHelper,
    private readonly wagoApiClient: WagoApiClient,
    private readonly articleDataService: ArticleDataService,
    private readonly projectModel: ProjectModel,
    private readonly projectModelService: ProjectModelService,
    private readonly projectArticleService: ProjectArticleService,
    private readonly undoRedoManage: UndoRedoManager,
    private readonly articleManager: ArticleManager,
    private readonly articleObject3DManager: ArticleObject3DManager,
    private readonly projectArticleManager: ProjectArticleManager,
    private readonly selectionModelService: SelectionModelService,
    private readonly insertArticleService: InsertArticleService,
    private readonly compositeArticleUtils: CompositeArticleUtils,
    private readonly containerService: ContainerService,
    private readonly admintoolComponentsPresenter: AdmintoolComponentsPresenter,
    private readonly queryParamsService: QueryParamsService,
    private readonly toastrService: ToastrService,
    private readonly localeResourceManager: LocaleResourceManager,
    private readonly globalContainer: GlobalContainer,
  ) {
    this.projectModel.projectChanged$.subscribe({
      next: (projectChanged) => this.projectChanged(projectChanged),
      error: (error) => Log.error('Error in projectModelChanged subscription: ', error),
    });

    this.articleSaved$.subscribe({
      next: () => this.updateCurrentArticleVO(),
      error: (error) => Log.error('Error in articleSaved subscription: ', error),
    });
  }

  private projectChanged(projectChanged: ProjectChanged): void {
    if (projectChanged.type === ProjectChangedType.ProjectRefreshed) {
      this.updateCurrentArticleVO();
    }
  }

  private updateCurrentArticleVO(): void {
    let articleVO: ArticleVO;
    const articleUsage: ArticleUsageVO = this.projectModel.getCurrentLoadedArticleUsage();
    if (articleUsage) {
      articleVO = this.projectArticleManager.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
    }
    this.currentArticleVO.set(articleVO);
  }


  public saveArticle(model: ArticleVO, data: OBJRepresentationDataVO): Observable<unknown> {
    const dto: ExportArticleDTO = ExportArticleDTO.create(model, data);
    return this.wagoApiClient.post('admintool/article/save', dto).pipe(
      tap(() => {
        this.projectModel.resetAdminProjectChanged();
        this.articleSavedSubject.next();
      }));
  }

  public saveArticleProperties(model: ArticleVO): Observable<unknown> {
    return this.wagoApiClient.post('admintool/article/save-properties', model).pipe(
      tap(() => {
        this.projectModel.resetAdminProjectChanged();
        this.articleSavedSubject.next();
      })
    );
  }

  public async loadArticle(name: string, version?: number, shouldUpdateUrl: boolean = true): Promise<void> {
    this.selectionModelService.removeAllSelections();
    this.articleManager.clearArticleCache();
    this.articleObject3DManager.clearAllCaches();
    this.projectModel.compositeAdminBaseArticleUsage = null;
    this.globalContainer.resetCoordianteSystem();

    let articles: ArticleVO[];

    const isArticleId = ArticleUtils.isStringArticleId(name);

    try {
      articles = isArticleId
        ? [await lastValueFrom(this.articleDataService.loadArticleByArticleId(+name, version))]
        : await lastValueFrom(this.articleDataService.loadArticlesByArticleNumber(name, version));

    } catch (error) {
        Log.error('Could not load article: ', error);
    }

    if (articles.length == 0 || !articles[0]) {
      this.alertHelper.showError('searchArticleView.title.no_articles_found', 'searchArticleView.message.no_articles_found', { translationParams: [name]});
      return;
    }

    const article = articles.length > 1
      ? await this.admintoolComponentsPresenter.selectFromMultipleFoundArticles(articles)
      : articles[0];

    if(!article) {
      return;
    }

    this.articleNoLoadedSubject.next(article.articleNo);

    this.undoRedoManage.clearAll();

    if (ArticleUtils.isArticleWithout3DRepresentation(article) && !ArticleUtils.isCompositeArticle(article)) {
      this.alertHelper.showError('searchArticleView.title.no_representation', 'searchArticleView.message.no_representation');
    }

    this.projectModelService.createNewProject();
    this.projectModelService.currentProjectType = "ADMIN";
    this.projectModel.currentProject.name = 'AdminToolProject';
    this.projectModel.isCompositeAdminView = ArticleUtils.isCompositeArticle(article);
    if (this.projectModel.isCompositeAdminView) {
      this.projectModel.compositeAdminBaseArticleUsage = this.projectArticleService.createUnstoredNewArticleUsage(article.identity, article.version);
    }
    if (shouldUpdateUrl) {
      this.updateUrlLocation(name, version);
    }

    if (!this.projectModel.isCompositeAdminView && this.isArticleContainsDuplicateOrderNumbers(article)) {
      this.alertHelper.showError('admintool.duplicate_order_numbers.title', 'admintool.duplicate_order_numbers.load.message');
    }

    const insertedData = await this.insertArticleService.insertArticleByArticleLocation(article, new CentreArticleLocationVO());
    if (!insertedData) {
      Log.error('Could not insert article');
    }
  }

  public async saveArticleHandler(): Promise<boolean> {
    let articleVO: ArticleVO;
    const articleUsage: ArticleUsageVO = this.projectModel.getCurrentLoadedArticleUsage();
    if (articleUsage) {
      articleVO = this.projectArticleManager.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
    }
    if (!articleVO) {
      return true;
    } else {
      if (ArticleUtils.isCompositeArticle(articleVO)) {
        await lastValueFrom(this.saveArticleProperties(articleVO));
        this.toastrService.success(this.localeResourceManager.getValueByKey("toast.message.saved"));
        return true;
      } else {
        const continueSave = await this.checkDuplicateOrderNumbers(articleVO);
        if (continueSave) {
          const saved = await this.admintoolComponentsPresenter.openArticleVersionDialog(articleVO);
          if(saved) {
            this.toastrService.success(this.localeResourceManager.getValueByKey("toast.message.saved"));
          }
          return saved;
        } else {
          return false;
        }
      }
    }
  }

  public checkDuplicateOrderNumbers(articleVO: ArticleVO): Promise<boolean> {
    const continueSave: boolean = true;
    if (this.isArticleContainsDuplicateOrderNumbers(articleVO)) {
        return this.admintoolComponentsPresenter.openDuplicatedOrderNumbersDialog();
    } else {
        return Promise.resolve(continueSave);
    }
}

public isArticleContainsDuplicateOrderNumbers(articleVO: ArticleVO): boolean {
    if (!articleVO) {
        return false;
    }
    const metaElements: MetaElementVO[] = MetaElementUtils.excludeInternalMetaElements(articleVO.metaElements);
    const orderNumbers: number[] = metaElements.map((metaElement: MetaElementVO) => metaElement.orderNumber);
    return orderNumbers.length !== new Set(orderNumbers).size;
}

  public updateArticleLevels(onlyCheckStatus: boolean): Observable<boolean> {
    const params: HttpParams = new HttpParams().set('onlyCheckStatus', onlyCheckStatus.toString());
    const overlayType = this.getOverlay(onlyCheckStatus);
    return this.wagoApiClient.post('admintool/update-article-levels', undefined, { params }, { overlayType });
  }

  public updateCarrierConnectableArticle(onlyCheckStatus: boolean): Observable<boolean> {
    const params: HttpParams = new HttpParams().set('onlyCheckStatus', onlyCheckStatus.toString());
    const overlayType = this.getOverlay(onlyCheckStatus);
    return this.wagoApiClient.post(
      'admintool/update-carrier-connectable-articles',
      undefined,
      { params },
      { overlayType }
    );
  }

  public updateAlternativeGroupMapping(onlyCheckStatus: boolean): Observable<boolean> {
    const params: HttpParams = new HttpParams().set('onlyCheckStatus', onlyCheckStatus.toString());
    const overlayType = this.getOverlay(onlyCheckStatus);
    return this.wagoApiClient.post('admintool/update-alternative-group-mapping', undefined, { params }, { overlayType });
  }

  public updateCompositeArticles(onlyCheckStatus: boolean, file: ArrayBuffer): Observable<UpdateCompositeArticleResult> {
    const params: HttpParams = new HttpParams().set('onlyCheckStatus', onlyCheckStatus.toString());
    const overlayType = this.getOverlay(onlyCheckStatus);
    return this.wagoApiClient.post('admintool/update-composite-articles', file, { params }, { overlayType }).pipe(
      map(data => this.modelConversionService.convertUpdateCompositeArticleResult(data))
    );
  }

  public replaceInnerCompositeArticles(onlyCheckStatus: boolean, file: ArrayBuffer): Observable<boolean> {
    const params: HttpParams = new HttpParams().set('onlyCheckStatus', onlyCheckStatus.toString());
    const overlayType = this.getOverlay(onlyCheckStatus);
    return this.wagoApiClient.post('admintool/replace-inner-composite-articles', file, { params }, { overlayType });
  }

  public updateInitialRotation(onlyCheckStatus: boolean, file: ArrayBuffer): Observable<boolean> {
    const params: HttpParams = new HttpParams().set('onlyCheckStatus', onlyCheckStatus.toString());
    const overlayType = this.getOverlay(onlyCheckStatus);
    return this.wagoApiClient.post('admintool/update-initial-rotation', file, { params }, { overlayType });
  }

  public getAccessoryEntries(articleId: number): Observable<ArticleAccessoryEntryVO[]> {
    return this.wagoApiClient.get(`admintool/article/${articleId}/accessories`).pipe(map((data) => this.modelConversionService.convertArticleAccessoryEntryVOs(data)));
  }

  public saveAccessoryEntries(articleId: number, accessories: ArticleAccessoryEntryVO[]): Observable<void> {
    return this.wagoApiClient.post(`admintool/article/${articleId}/accessories`, accessories);
  }

  public getArticleMetadata(articleId: number): Observable<ArticleMetadataVO> {
    return this.wagoApiClient.get(`admintool/article/${articleId}/article-metadata`).pipe(map((data) => this.modelConversionService.convertArticleMetadataVO(data)));
  }

  public updateArticleMetadata(articleMetadata: ArticleMetadataVO): Observable<ArticleMetadataVO> {
    return this.wagoApiClient.post('admintool/article/article-metadata', articleMetadata).pipe(map((data) => this.modelConversionService.convertArticleMetadataVO(data)));
  }

  public getArticleEndPlateInfo(articleNo: string): Observable<ArticleEndPlateInfoVO[]> {
    let params: HttpParams = new HttpParams();
    params = params.append('articleNo', articleNo);
    const requestOptions = {
      params: params,
    };
    return this.wagoApiClient.get('admintool/article/endplateinfo', requestOptions).pipe(map((data) => this.modelConversionService.convertArticleEndPlateInfoVOs(data)));
  }

  public saveEndPlateEntries(endPlateInfos: ArticleEndPlateInfoVO[]): Observable<void> {
    return this.wagoApiClient.post('admintool/article/update-endplates', endPlateInfos);
  }

  public deleteArticleVersion(articleId: number, version: number): Observable<void> {
    let params: HttpParams = new HttpParams();
    params = params.append('articleId', articleId);
    params = params.append('version', version.toString());
    const requestOptions = {
      params: params,
    };
    return this.wagoApiClient.delete('admintool/article/versions', requestOptions);
  }

  public deleteArticle(articleId: number): Observable<void> {
    let params: HttpParams = new HttpParams();
    params = params.append('articleId', articleId);
    const requestOptions = {
      params: params,
    };
    return this.wagoApiClient.delete('admintool/article', requestOptions);
  }

  private getOverlay(checkStatus: boolean): OverlayType {
    return checkStatus ? null : OverlayBackground.transparent;
  }

  private getCurrentlyLoadedComplexArticleUsages(): ArticleContainer[] {
    return this.containerService.enumerateArticleContainersInGlobal();
  }

  public getDataForTreeStructure(): TreeAssistantNode[] {
    const articleContainers: ArticleContainer[] = this.getCurrentlyLoadedComplexArticleUsages();
    return this.parseDataForTreeStructure(articleContainers, 0);
  }

  private parseDataForTreeStructure(containers: ArticleContainer[], index: number): TreeAssistantNode[] {
    const result: TreeAssistantNode[] = [];
    const proceseedContainers: ArticleContainer[] = [];

    containers.forEach((container: ArticleContainer) => {
      if (proceseedContainers.indexOf(container) < 0) {
        const compositeArticleIds: string[] = this.compositeArticleUtils.getCompositePartArticleUsageIdsByIndex(container, index);
        const compositeParts: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(compositeArticleIds);

        if (compositeParts.length) {
          const rootTreeElement: TreeAssistantNode = {
            title: this.compositeArticleUtils.getCompositeArticleNoFromUsageByIndex(compositeParts[0].articleUsage, index),
            children: this.parseDataForTreeStructure(compositeParts, index + 1),
            nodeType: NodeType.FOLDER,
            guid: container.articleGUID,
            open: true,
          };
          proceseedContainers.push(...compositeParts);
          result.push(rootTreeElement);
        } else {
          const treeElement: TreeAssistantNode = {
            title: container.article.articleNo,
            children: [],
            nodeType: NodeType.ITEM,
            guid: container.articleGUID,
            open: true,
          };
          proceseedContainers.push(container);
          result.push(treeElement);
        }
      }
    });
    return result;
  }

  public isPropertyUsedByCurrentlyLoadedArticle(selectedPropertyType: string, propertyName: string): boolean {
    const articleUsage: ArticleUsageVO = this.projectModel.getCurrentLoadedArticleUsage();
    if (articleUsage) {
      const articleVO: ArticleVO = this.projectArticleManager.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
      let result: boolean;
      if (selectedPropertyType === PropertyDefTypes.BEHAVIOR) {
        result = !!articleVO.behaviorProperties.find((property: PropertyVO) => {
          return propertyName === property.name;
        });
      } else if (selectedPropertyType === PropertyDefTypes.PLAUSI) {
        result = !!articleVO.plausiProperties.find((property: PropertyVO) => {
          return propertyName === property.name;
        });
      } else {
        const connectionProperties: PropertyVO[] = articleVO.connectionFeatures.concat(...articleVO.connectionRequirements);
        result = !!connectionProperties.find((property: PropertyVO) => {
          return propertyName === property.name;
        });
      }
      return result;
    } else {
      return false;
    }
  }

  public loadArticleInNewBrowserTab(name: string): void {
    window.open(this.getLoadArticleUrl(name), `_blank`);
  }

  private getLoadArticleUrl(name: string): string {
    const param: string = ArticleUtils.isStringArticleId(name) ? 'articleId' : 'articleNo';
    const preparedName: string = this.getArticleNameForUrl(name);
    return CommonUtils.baseLocation() + `?${this.queryParamsService.createFunctionUrlParameter('loadarticle')}&${param}=${preparedName}`;
  }

  private getArticleNameForUrl(name: string) {
    return StringUtils.replaceAll(name, '/', '_');
  }

  public getArticleNameForLoad(name: string) {
    return StringUtils.replaceAll(name, '_', '/');
  }

  private updateUrlLocation(name: string, version?: number): void {
    history.pushState({}, null, this.getLoadArticleUrl(name) + (version !== undefined ? `&version=${version}` : ''));
  }

  public searchArticlesWithMultiConnectionsBySeries(series: string): Observable<ArticleWithMultiConnections[]> {
    const parameters: Parameters = { overlayType: null };
    return this.wagoApiClient.get<ArticleWithMultiConnections[]>(`admintool/article/${series}/with-multi-connections`, undefined, parameters);
  }
}
